// ABOUTME: Application shell with top navigation bar and content area.
// ABOUTME: Uses Clerk components to show user account button or sign-in button based on auth state.
import { useState } from 'react'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import MuiLink from '@mui/material/Link'
import IconButton from '@mui/material/IconButton'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import MenuIcon from '@mui/icons-material/Menu'
import { Show, UserButton, SignInButton } from '@clerk/react'
import { Outlet, Link } from 'react-router'
import { DaublyLogo } from './DaublyLogo'
import { PageShell, PageContent, NavSpacer, NavGap, NavLogoLink, AppFooter, MobileOnly, DesktopOnly, NavDrawerList } from '../theme'
import { useIdentify } from '../analytics/useIdentify'

const NAV_LINKS = [
  { label: 'Host', to: '/games' },
  { label: 'Square Packs', to: '/square-packs' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Play', to: '/play' },
]

export function AppLayout() {
  useIdentify()
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <PageShell>
      <AppBar position="static">
        <Toolbar>
          <NavLogoLink component={Link} to="/">
            <DaublyLogo />
          </NavLogoLink>
          <NavGap />

          {/* Desktop nav links — left side */}
          <DesktopOnly>
            <Show when="signed-in">
              <Button color="inherit" component={Link} to="/games">Host</Button>
              <Button color="inherit" component={Link} to="/square-packs">Square Packs</Button>
            </Show>
            <Button color="inherit" component={Link} to="/pricing">Pricing</Button>
          </DesktopOnly>

          <NavSpacer />

          {/* Desktop nav link — right side, plus account controls */}
          <Show when="signed-in">
            <DesktopOnly>
              <Button color="inherit" component={Link} to="/play">Play</Button>
              <NavGap />
            </DesktopOnly>
            <UserButton />
            <MobileOnly ml={1}>
              <IconButton color="inherit" aria-label="open menu" onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
            </MobileOnly>
          </Show>

          <Show when="signed-out">
            <MobileOnly>
              <Button color="inherit" component={Link} to="/pricing">Pricing</Button>
            </MobileOnly>
            <SignInButton>
              <Button color="inherit">Sign In</Button>
            </SignInButton>
          </Show>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer — nav links as real anchor elements for accessibility */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <NavDrawerList>
          <List>
            {NAV_LINKS.map((link) => (
              <ListItem key={link.label} disablePadding>
                <ListItemButton component={Link} to={link.to} onClick={() => setDrawerOpen(false)}>
                  <ListItemText primary={link.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </NavDrawerList>
      </Drawer>

      <PageContent>
        <Container>
          <Outlet />
        </Container>
      </PageContent>

      <AppFooter>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            © 2026 Daubly
            {' · '}
            <MuiLink component={Link} to="/privacy" color="inherit">Privacy Policy</MuiLink>
            {' · '}
            <MuiLink component={Link} to="/terms" color="inherit">Terms of Service</MuiLink>
            {' · '}
            <MuiLink component={Link} to="/pricing" color="inherit">Pricing</MuiLink>
            {' · '}
            <MuiLink component={Link} to="/contact" color="inherit">Contact</MuiLink>
          </Typography>
        </Container>
      </AppFooter>
    </PageShell>
  )
}
